import { DB_NAME, DB_VERSION } from '../../../libraries/storage/constants';
import { initDB } from '../../../libraries/storage/idb';
import { ORCHESTRATOR_INTERVAL_MS } from './constants';
import type { TransactionalIndexedDBWriter } from './transactional-indexed-db-writer';
type RollbackNotificationRecipient = () => void;
class TransactionRollbackNotifier {
  private rollbackNotificationRecipients: RollbackNotificationRecipient[] = [];
  addNotificationRecipient(recepient: RollbackNotificationRecipient) {
    this.rollbackNotificationRecipients.push(recepient);
  }
  notifyAll() {
    this.rollbackNotificationRecipients.forEach(recepient => {
      try {
        recepient();
      } catch {}
    });
  }
}
export class PersistenceOrchestrator {
  private readonly writers: readonly TransactionalIndexedDBWriter[];
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private flushInProgress = false;
  constructor(writers: readonly TransactionalIndexedDBWriter[]) {
    this.writers = writers;
    for (const writer of this.writers) {
      writer.setWritePetitionReceiver(() => this.onPetition());
    }
  }
  start(): void {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => {
      this.flushToIdb();
    }, ORCHESTRATOR_INTERVAL_MS);
  }
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  async flushToIdb(): Promise<void> {
    if (this.flushInProgress || this.writers.length === 0) {
      return;
    }
    const storeNames = [...new Set(this.writers.flatMap(w => [...w.getStores()]))];
    if (storeNames.length === 0) {
      return;
    }
    let haveRollbackNotifiersInvoked = false;
    let resolveFn: (() => void) | null = null;
    let rejectFn: ((e: Error | DOMException | null | undefined) => void) | null = null;
    const promise = new Promise<void>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });
    const rollbackNotifier = new TransactionRollbackNotifier();
    const notifyRollback = () => {
      if (!haveRollbackNotifiersInvoked) {
        haveRollbackNotifiersInvoked = true;
        rollbackNotifier.notifyAll();
      }
    };
    const markError = (error: Error | DOMException | null | undefined) => {
      this.flushInProgress = false;
      rejectFn?.(error);
      notifyRollback();
    };
    let db: IDBDatabase | null = null;
    let tx: IDBTransaction | undefined;
    try {
      this.flushInProgress = true;
      db = await this.openDb();
      tx = db?.transaction(storeNames, 'readwrite');
      for (const w of this.writers) {
        w.writeToIdb(tx, (recepient: RollbackNotificationRecipient) => {
          rollbackNotifier.addNotificationRecipient(recepient);
        });
      }
      tx.oncomplete = () => {
        this.flushInProgress = false;
        resolveFn?.();
      };
      tx.onabort = () => {
        markError(tx?.error);
      };
      tx.commit();
    } catch (err) {
      // tx.abort() is not needed here because if the commit fails, the transaction will be aborted automatically.
      markError(err);
    } finally {
      db?.close();
    }
    return promise;
  }
  dispose(): void {
    this.stop();
  }
  private onPetition(): void {
    this.flushToIdb();
  }
  private async openDb(): Promise<IDBDatabase> {
    await initDB();
    return new Promise((resolve, reject) => {
      const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}